import { Box, IconButton, Link, Portal, Stack } from "@mui/material";
import { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavButton from "./navButton";
import HamburgerMenuIcon from "./hamburgerMenuIcon";
import SocialNav from "./socialNav";
import logo from '../assets/new-tkfm-transparent.png';
import useModal from "../hooks/useModal";

type NavMenuItem = {
	key: string;
	label: string;
	external?: boolean;
	path: string;
};

type Props = {
	mode?: 'desktop' | 'mobile'
};

const MainNav: FC<Props> = ({ mode = 'desktop' }) => {
	const navigate = useNavigate();
	const { hide: hideModal, modals } = useModal();

	const menuItems = useRef<NavMenuItem[]>([
		{ key: 'tunes', label: 'Tunes', path: '/tunes' },
		{ key: 'albums', label: 'Albums', path: '/albums' },
		{ key: 'tags', label: 'All Tags', path: '/tags' },
		{ key: 'blog', label: 'Blog', path: '/blog' },
		{ key: 'affiliates', label: 'Affiliates', path: '/affiliates' },
		{ key: 'faq', label: 'FAQ', path: '/faq' },
		{ key: 'about', label: 'About', path: '/about' }
	]);

	const [currentActiveKey, setCurrentActiveKey] = useState<string | null>(null);
	const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);

	useEffect(() => {
		const activeItem = menuItems.current.find(item => item.path === window.location.pathname);

		if (activeItem) {
			setCurrentActiveKey(activeItem.key);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menuItems, setCurrentActiveKey, window.location.pathname]);

	const handleMobileMenuToggle = (event: MouseEvent) => {
		event.preventDefault();
		setShowMobileMenu(!showMobileMenu);
	};

	const handleNavigation = (item: NavMenuItem) => () => {
		if (item.external) {
			window.open(item.path, '_blank');
		} else {
			if (modals && modals.length > 0) {
				hideModal();

				setTimeout(() => {
					navigate(item.path);
				});
			} else {
				navigate(item.path);
			}
		}

		setShowMobileMenu(false);
	};

	return (
		<>
			{mode === 'desktop' && (
				<Stack sx={{ paddingY: 2 }}>
					{menuItems.current.map((item, index) => (
						<NavButton key={index} label={item.label} active={item.key === currentActiveKey} onClick={handleNavigation(item)} />
					))}
				</Stack>
			)}
			{mode === 'mobile' && (
				<>
					<Box sx={{ width: 41, height: 41 }} />
					<Portal container={() => document.getElementById('mobileMenuPortal')}>
						<Box
							sx={{
								position: 'fixed',
								top: 0,
								right: 0,
								bottom: 0,
								left: 0,
								backdropFilter: 'blur(10px) saturate(1.2)',
								backgroundColor: 'rgba(0, 0, 0, 0.6)',
								py: 1,
								opacity: showMobileMenu ? 1 : 0,
								pointerEvents: showMobileMenu ? 'auto' : 'none',
								transition: 'opacity 0.5s ease',
								zIndex: 1302
							}}
						>
							{showMobileMenu && (
								<>
									<Stack mt={1} mb={2} px={2} spacing={2} alignItems="center">
										<Box sx={{ flex: '0 0 auto', textAlign: 'center' }}>
											<Link href="/">
												<Box component="img" src={logo} height={38} />
											</Link>
										</Box>
										<Stack width="100%">
											{menuItems.current.map((item, index) => (
												<NavButton
													key={`navMenuItem-${index}`}
													label={item.label}
													mode="mobile"
													animated={true}
													index={index}
													active={item.key === currentActiveKey}
													onClick={handleNavigation(item)}
												/>
											))}
										</Stack>
									</Stack>
									<SocialNav mode="mobile" animationDelay={menuItems.current.length * 0.05 + 0.2} />
								</>
							)}
						</Box>
						<IconButton
							size="medium"
							sx={{
								right: { xs: 40, sm: 40, md: 72, lg: 'calc(8.333% + 40px', xl: 'calc(16.667% + 40px' },
								display: { xs: 'block', sm: 'block', md: 'none' },
							}}
							style={{
								position: 'fixed',
								top: 17,
								width: 41,
								height: 41,
								zIndex: 1303
							}}
							onClick={handleMobileMenuToggle}
						>
							<HamburgerMenuIcon toX={showMobileMenu} />
						</IconButton>
					</Portal>
				</>
			)}
		</>
	);
};

export default MainNav;
